import { uploadPublicFile } from '@/modules/upload/services/uploadService'
import { validateCommunityAttachmentFile } from '@/modules/comunidade/utils/communityAttachments'
import {
  addChatPostAttachments,
  type ChatPostAttachment,
} from './chatPostAttachmentsService'

const CHAT_POST_ATTACHMENT_BUCKETS = ['assets']

function buildAttachmentFolder(chatId: number): string {
  return `chat/posts/${chatId}`
}

export type UploadChatPostAttachmentsResult = {
  attachments: ChatPostAttachment[]
  uploadedCount: number
  failedCount: number
}

export async function uploadAndAddChatPostAttachments(
  chatId: number,
  files: File[],
): Promise<UploadChatPostAttachmentsResult> {
  const id = Number(chatId)
  if (!Number.isFinite(id) || id <= 0) {
    return { attachments: [], uploadedCount: 0, failedCount: 0 }
  }

  const list = (Array.isArray(files) ? files : []).filter(Boolean)
  if (list.length === 0) {
    return { attachments: [], uploadedCount: 0, failedCount: 0 }
  }

  const validFiles = list.filter((file) => validateCommunityAttachmentFile(file).ok)
  if (validFiles.length === 0) {
    return { attachments: [], uploadedCount: 0, failedCount: list.length }
  }

  const folder = buildAttachmentFolder(id)
  const uploaded: {
    file: File
    url: string
    bucket: string
    path: string
  }[] = []
  let failedCount = 0

  for (const file of validFiles) {
    try {
      const { url, bucket, path } = await uploadPublicFile(
        file,
        folder,
        CHAT_POST_ATTACHMENT_BUCKETS,
      )
      uploaded.push({ file, url, bucket, path })
    } catch (error) {
      failedCount += 1
      // eslint-disable-next-line no-console
      console.error('[Chat] upload post attachment error', error)
    }
  }

  if (uploaded.length === 0) {
    return { attachments: [], uploadedCount: 0, failedCount }
  }

  const attachments = await addChatPostAttachments(
    id,
    uploaded.map((item) => ({
      url: item.url,
      storageBucket: item.bucket,
      storagePath: item.path,
      contentType: item.file.type || null,
      sizeBytes: typeof item.file.size === 'number' ? item.file.size : null,
    })),
  )

  return {
    attachments,
    uploadedCount: uploaded.length,
    failedCount,
  }
}
